import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../auth/AuthContext';
import { usePoints } from '../context/PointsContext';
import './Footer.css';

const Footer = () => {
  const { user, getFreshToken } = useAuth();
  const { triggerPointsAnimation } = usePoints();
  const [clickedNetworks, setClickedNetworks] = useState([]);

  const handleSocialClick = async (e, network, url) => {
    const clickX = e.clientX;
    const clickY = e.clientY;
    window.open(url, '_blank');

    if (user && !clickedNetworks.includes(network)) {
      try {
        const token = await getFreshToken();
        if (!token) return;
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/points/social-click`,
          { network },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.rewarded) {
          setClickedNetworks(prev => [...prev, network]);
          triggerPointsAnimation(30, clickX, clickY);
        }
      } catch (error) {
        console.error('Erro ao registrar ponto social:', error);
      }
    }
  };

  const socialLinks = [
    {
      id: 'instagram',
      icon: 'fab fa-instagram',
      url: 'https://www.instagram.com/ofertzz?igsh=aG9kZmo0NHo5b3Rm',
      label: 'Instagram',
      color: '#E1306C',
    },
    {
      id: 'youtube',
      icon: 'fab fa-youtube',
      url: 'https://www.youtube.com/@Ofertiz',
      label: 'YouTube',
      color: '#FF0000',
    },
    {
      id: 'tiktok',
      icon: 'fab fa-tiktok',
      url: 'https://vm.tiktok.com/ZS98YhJvVwg5K-Skvp5/',
      label: 'TikTok',
      color: '#ffffff',
    },
  ];

  const navLinks = [
    { to: '/', label: 'Home', icon: 'fa-house' },
    { to: '/recentes', label: 'Ofertas Recentes', icon: 'fa-bolt' },
    { to: '/blog', label: 'Blog', icon: 'fa-newspaper' },
    { to: '/perfil', label: 'Minha Conta', icon: 'fa-circle-user' },
    { to: '/perfil', state: { activeTab: 'favoritos' }, label: 'Favoritos', icon: 'fa-heart' },
    { to: '/perfil', state: { activeTab: 'carteira' }, label: 'Ofertiz Coins', icon: 'fa-coins' },
  ];

  const legalLinks = [
    { to: '/politica-de-privacidade', label: 'Política de Privacidade', icon: 'fa-shield-halved' },
    { to: '/termos-de-uso', label: 'Termos de Uso', icon: 'fa-file-contract' },
    {
      href: 'mailto:contato@ofertiz.com.br?subject=LGPD — Direitos do Titular',
      label: 'Canal de Privacidade (LGPD)',
      icon: 'fa-user-shield',
    },
  ];

  return (
    <footer className="footer-root">
      {/* Linha decorativa no topo */}
      <div className="footer-top-bar" />

      <div className="container">
        {/* Grid principal */}
        <div className="footer-grid">

          {/* Col 1 — Brand */}
          <div className="footer-brand-col">
            <div className="footer-logo">
              <span className="footer-logo-tag">%</span>
              <span className="footer-logo-text">Ofertiz</span>
            </div>
            <p className="footer-tagline">
              Seu radar de ofertas. Descubra os melhores descontos antes que acabem.
            </p>

            {/* Redes Sociais */}
            <div className="footer-socials">
              {socialLinks.map(({ id, icon, url, label, color }) => (
                <button
                  key={id}
                  onClick={(e) => handleSocialClick(e, id, url)}
                  className={`footer-social-btn ${clickedNetworks.includes(id) ? 'footer-social-btn--done' : ''}`}
                  title={`${label}${user ? ' (+30 moedas)' : ''}`}
                  style={{ '--social-color': color }}
                >
                  <i className={icon} />
                  {clickedNetworks.includes(id) && (
                    <span className="footer-social-check">
                      <i className="fas fa-check" />
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Recompensa hint */}
            {user && (
              <p className="footer-coins-hint">
                <i className="fas fa-coins" /> Ganhe <strong>+30 moedas</strong> por cada rede seguida!
              </p>
            )}
          </div>

          {/* Col 2 — Navegação */}
          <div className="footer-col">
            <h4 className="footer-col-title">
              <span className="footer-col-dot" />
              Navegação
            </h4>
            <ul className="footer-link-list">
              {navLinks.map(({ to, state, label, icon }) => (
                <li key={label}>
                  <Link to={to} state={state} className="footer-link">
                    <i className={`fas ${icon} footer-link-icon`} />
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 3 — Legal */}
          <div className="footer-col">
            <h4 className="footer-col-title">
              <span className="footer-col-dot" />
              Legal & Privacidade
            </h4>
            <ul className="footer-link-list">
              {legalLinks.map(({ to, href, label, icon }) => (
                <li key={label}>
                  {to ? (
                    <Link to={to} className="footer-link">
                      <i className={`fas ${icon} footer-link-icon`} />
                      {label}
                    </Link>
                  ) : (
                    <a href={href} className="footer-link">
                      <i className={`fas ${icon} footer-link-icon`} />
                      {label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Col 4 — Contato */}
          <div className="footer-col">
            <h4 className="footer-col-title">
              <span className="footer-col-dot" />
              Contato
            </h4>
            <ul className="footer-link-list">
              <li>
                <a href="mailto:contato@ofertiz.com.br" className="footer-link">
                  <i className="fas fa-envelope footer-link-icon" />
                  contato@ofertiz.com.br
                </a>
              </li>
              <li>
                <a href="tel:+5575981462701" className="footer-link">
                  <i className="fas fa-phone footer-link-icon" />
                  (75) 98146-2701
                </a>
              </li>
              <li>
                <a
                  href="https://api.whatsapp.com/send?phone=5575981462701"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-link footer-link--whatsapp"
                >
                  <i className="fab fa-whatsapp footer-link-icon" />
                  WhatsApp
                </a>
              </li>
            </ul>

            {/* Badge LGPD */}
            <div className="footer-lgpd-badge">
              <i className="fas fa-shield-halved" />
              <span>Site adequado à LGPD</span>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="footer-bottom">
          <span className="footer-copyright">
            © {new Date().getFullYear()} Ofertiz — Todos os direitos reservados.
          </span>
          <a
            href="https://www.instagram.com/devaspect"
            target="_blank"
            rel="noopener noreferrer"
            className="footer-devaspect"
          >
            <span>Dev</span><span className="footer-devaspect-accent">Aspect</span>
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
